import { Box, Flex, Grid, GridItem, Show, VStack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import type { MultiValue, SingleValue } from 'chakra-react-select';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftCheckbox from '@/components/Atoms/FaceliftCheckbox';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftSwitch from '@/components/Atoms/FaceliftSwitch/FaceliftSwitch';
import FaceliftText from '@/components/Atoms/FaceliftText';
import type { Board } from '@/components/Molecules/FaceliftDraggable/FaceliftDraggable';
import FaceliftDraggable from '@/components/Molecules/FaceliftDraggable/FaceliftDraggable';
import {
	environmentsItems,
	portItems,
	portRoleBasedPermissions,
	userTypeItems,
} from '@/constants';
import { useGetAccounts } from '@/hooks/service-hooks/accounts';
import { useGetLocations, useGetSalesReps } from '@/hooks/service-hooks/common';
import {
	useGetAllNotificationTypes,
	useGetAllPermissions,
} from '@/hooks/service-hooks/users';
import { usePaginatedSelect } from '@/hooks/usePaginatedSelect';
import type { Account } from '@/types/account';
import type { LocationData } from '@/types/location';
import type { SalesRep } from '@/types/reps';
import type {
	UserNotificationItem,
	UserNotificationMethod,
} from '@/types/users';
import type { UsersRolesFormProps } from './types';

const usersRolesSchema = yup.object().shape({
	firstName: yup
		.string()
		.test(
			'no-leading-spaces',
			'No leading spaces allowed',
			(value) => !value || value === value.trimStart(),
		)
		.required('First Name is required'),
	lastName: yup
		.string()
		.test(
			'no-leading-spaces',
			'No leading spaces allowed',
			(value) => !value || value === value.trimStart(),
		)
		.required('Last Name is required'),
	email: yup
		.string()
		.test(
			'no-leading-spaces',
			'No leading spaces allowed',
			(value) => !value || value === value.trimStart(),
		)
		.email('Invalid email format')
		.required('Email is required'),
	port: yup
		.string()
		.test(
			'no-leading-spaces',
			'No leading spaces allowed',
			(value) => !value || value === value.trimStart(),
		)
		.required('Port is required'),
	role: yup
		.string()
		.test(
			'no-leading-spaces',
			'No leading spaces allowed',
			(value) => !value || value === value.trimStart(),
		)
		.required('Role is required'),
	environments: yup
		.array()
		.of(yup.string())
		.required('Environments is required'),
	clinics: yup
		.array()
		.of(yup.object<SelectItem>())
		.required('Clinics is required'),
	userType: yup
		.string()
		.test(
			'no-leading-spaces',
			'No leading spaces allowed',
			(value) => !value || value === value.trimStart(),
		)
		.required('User Type is required'),
	salesReps: yup
		.array()
		.of(yup.object<SelectItem>())
		.when('port', ([port], schema) =>
			port === 'SalesRep'
				? schema.required('Sales Reps is required')
				: schema.default([]),
		),
	consignments: yup.array().of(yup.object<SelectItem>()).default([]),
	autoSyncClinics: yup.boolean(),
	status: yup.string(),
	permissions: yup.array().of(yup.string()).default([]),
	notificationTypes: yup
		.array()
		.of(yup.object<UserNotificationItem>())
		.default([]),
	notificationsEnabled: yup.boolean().default(false),
});

type UsersRolesFormData = yup.InferType<typeof usersRolesSchema>;

const UsersRolesForm: React.FC<UsersRolesFormProps> = ({
	mode,
	initialData,
	onSubmit,
	isLoading = false,
	onIsDirtyChange,
}) => {
	const [hasPortRoleChanged, setHasPortRoleChanged] = useState(false);
	const isManualPermissionChange = useRef(false);
	const [permissionsData, setPermissionsData] = useState<Board>({
		item: {},
		columns: {
			'column-1': {
				id: 'column-1',
				title: 'Available Permissions',
				itemIds: [],
			},
			'column-2': {
				id: 'column-2',
				title: 'Current Permissions',
				itemIds: [],
			},
		},
		columnOrder: ['column-1', 'column-2'],
	});

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		reset,
		getValues,
	} = useForm<UsersRolesFormData>({
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		resolver: yupResolver(usersRolesSchema) as any,
		mode: 'onSubmit',
		defaultValues: {
			firstName: '',
			lastName: '',
			email: '',
			port: '',
			role: '',
			environments: [],
			clinics: [],
		},
	});

	const clinics = usePaginatedSelect({
		queryHook: useGetAccounts,
		queryParams: { enabled: true },
		transform: (account: Account) => ({
			value: account.accountid,
			label: account.name,
		}),
		errorMessage: 'Unable to load clinics',
	});
	const locations = usePaginatedSelect({
		queryHook: useGetLocations,
		queryParams: { enabled: true },
		transform: (location: LocationData) => ({
			value: location.id,
			label: location.name,
		}),
		errorMessage: 'Unable to load locations',
	});
	const salesReps = usePaginatedSelect({
		queryHook: useGetSalesReps,
		queryParams: { enabled: true },
		transform: (salesRep: SalesRep) => ({
			value: salesRep.Account_ID.replace(/^\{|\}$/g, ''), // remove curly braces from the account id
			label: salesRep.AccountName,
		}),
		errorMessage: 'Unable to load sales reps',
	});

	const { data: fetchedPermissions, isFetching: isPermissionsFetching } =
		useGetAllPermissions({ enabled: mode === 'create' || mode === 'edit' });

	const {
		data: notificationTypesData,
		isFetching: isNotificationTypesFetching,
	} = useGetAllNotificationTypes(mode === 'create' || mode === 'edit');

	const notificationTypesItems = useMemo<SelectItem[]>(() => {
		if (!notificationTypesData) return [];
		return notificationTypesData.map((type) => ({
			label: type.module,
			value: type.module,
		}));
	}, [notificationTypesData]);

	const notificationTypesManagement = useMemo<
		Record<string, UserNotificationItem>
	>(() => {
		if (!notificationTypesData) return {};
		const types: Record<string, UserNotificationItem> = {};
		notificationTypesData.forEach((type) => {
			types[type.module] = {
				module: type.module,
				entityName: type.entityName,
				enabled: false,
				methods: type.methods.map((method) => ({
					name: method.name,
					action: method.action,
					enabled: false,
				})),
			};
		});
		return types;
	}, [notificationTypesData]);

	const portRoleMapping = useMemo<Record<string, string[]>>(
		() => ({
			GraftLogs: ['Admin', 'GraftLogs'],
			Mindsight: ['Admin', 'Contributor', 'Manager'],
			'PaymentsMSL': ['Admin', 'Manager'],
			'PaymentsWC+': ['Admin', 'Manager'],
			SalesRep: ['Admin', 'Rep'],
			'Woundcare+': ['Admin', 'Contributor', 'Manager', 'Accounting'],
		}),
		[],
	);

	const selectedPort = watch('port');
	const selectedRole = watch('role');
	const roleItems = useMemo<SelectItem[]>(
		() =>
			(portRoleMapping[selectedPort] || []).map((role) => ({
				label: role,
				value: role,
			})),
		[selectedPort, portRoleMapping],
	);

	// Auto-select "Admin" role when port is selected (only in create mode)
	const previousPortRef = useRef<string>('');
	useEffect(() => {
		if (mode === 'create' && selectedPort && selectedPort !== previousPortRef.current) {
			previousPortRef.current = selectedPort;
			if (portRoleMapping[selectedPort]?.includes('Admin')) {
				setValue('role', 'Admin', { shouldDirty: false });
			}
		}
	}, [selectedPort, portRoleMapping, setValue, mode]);

	useEffect(() => {
		if (fetchedPermissions?.items && fetchedPermissions.items.length > 0) {
			const item: Record<
				string,
				{ id: string; content: string; secondaryContent: string }
			> = {};
			const allItemIds: string[] = [];
			let column1ItemIds: string[] = [];
			let column2ItemIds: string[] = [];

			fetchedPermissions.items.forEach((permission) => {
				item[permission._id] = {
					id: permission._id,
					content: permission.name,
					secondaryContent: permission.description,
				};
				allItemIds.push(permission._id);
			});

			// If in edit mode and initialData has permissions, split them between columns
			if (
				mode === 'edit' &&
				initialData?.permissions &&
				initialData.permissions?.length > 0
			) {
				const initialPermissionNames = initialData.permissions;

				const initialPermissionIds = fetchedPermissions.items
					.filter((permission) =>
						initialPermissionNames.includes(permission.name),
					)
					.map((permission) => permission._id);

				column2ItemIds = initialPermissionIds;

				column1ItemIds = allItemIds.filter(
					(id) => !initialPermissionIds.includes(id),
				);
			} else {
				column1ItemIds = allItemIds;
				column2ItemIds = [];
			}

			setPermissionsData((prev) => ({
				...prev,
				item,
				columns: {
					...prev.columns,
					'column-1': {
						...prev.columns['column-1'],
						itemIds: column1ItemIds,
					},
					'column-2': {
						...prev.columns['column-2'],
						itemIds: column2ItemIds,
					},
				},
			}));
		}
	}, [fetchedPermissions, initialData, mode]);

	// Helper function to get permission key based on port and role
	const getPermissionKey = (port: string, role?: string): string[] => {
		const keys: string[] = [];
		const isAdminOrNoRole = !role || role === 'Admin';

		switch (port) {
			case 'GraftLogs':
				if (isAdminOrNoRole) {
					keys.push('GraftLogs', 'Admin-GraftLogs');
				} else if (role === 'GraftLogs') {
					keys.push('GraftLogs');
				}
				break;

			case 'SalesRep':
				if (isAdminOrNoRole) {
					keys.push('SalesRep', 'Admin-SalesRep');
				} else if (role === 'Rep') {
					keys.push('SalesRep');
				}
				break;

			case 'PaymentsMSL':
				if (isAdminOrNoRole) {
					keys.push('PaymentsMSL', 'Admin-MindSight-PaymentMSL-PaymentsWC+');
				} else if (role === 'Manager') {
					keys.push('PaymentsMSL');
				}
				break;

			case 'PaymentsWC+':
				if (isAdminOrNoRole) {
					keys.push('PaymentsWC+', 'Admin-MindSight-PaymentMSL-PaymentsWC+');
				} else if (role === 'Manager') {
					keys.push('PaymentsWC+');
				}
				break;

			case 'Mindsight':
				if (isAdminOrNoRole) {
					keys.push('Mindsight', 'MindsightContributor', 'Admin-MindSight-PaymentMSL-PaymentsWC+');
				} else if (role === 'Manager') {
					keys.push('Mindsight');
				} else if (role === 'Contributor') {
					keys.push('MindsightContributor');
				}
				break;

			case 'Woundcare+':
				if (isAdminOrNoRole) {
					keys.push('Woundcare+Accounting', 'Woundcare+Contributor', 'Woundcare+Manager', 'Admin-Woundcare+');
				} else if (role === 'Manager') {
					keys.push('Woundcare+Manager');
				} else if (role === 'Contributor') {
					keys.push('Woundcare+Contributor');
				} else if (role === 'Accounting') {
					keys.push('Woundcare+Accounting');
				}
				break;
		}

		return keys;
	};

	// Assign permissions when port is selected (with or without role)
	useEffect(() => {
		if (!fetchedPermissions?.items || !selectedPort) {
			return;
		}

		const permissionNameToId: Record<string, string> = {};
		fetchedPermissions.items.forEach((permission) => {
			permissionNameToId[permission.name] = permission._id;
		});

		// In edit mode, get the user's actual permissions from backend (initialData)
		// But only use them if port/role hasn't been changed
		const userActualPermissions =
			mode === 'edit' && initialData?.permissions && !hasPortRoleChanged
				? initialData.permissions
				: [];

		const currentPermissionIds: string[] = [];
		const availablePermissionIds: string[] = [];

		// Get port's base permissions (without role) - only if no role is selected
		if (!selectedRole) {
			const portKeys = getPermissionKey(selectedPort);
			portKeys.forEach((key) => {
				portRoleBasedPermissions[key]?.forEach((permissionName) => {
					const permissionId = permissionNameToId[permissionName];
					if (permissionId && !currentPermissionIds.includes(permissionId)) {
						// In edit mode, only add to current if it's in the user's actual permissions from backend
						if (mode === 'edit' && userActualPermissions?.length > 0) {
							if (userActualPermissions.includes(permissionName)) {
								currentPermissionIds.push(permissionId);
							} else {
								if (!availablePermissionIds.includes(permissionId)) {
									availablePermissionIds.push(permissionId);
								}
							}
						} else {
							currentPermissionIds.push(permissionId);
						}
					}
				});
			});
		}

		// Add all role-based permissions for this port to available permissions
		const availableRoles = portRoleMapping[selectedPort] || [];
		availableRoles.forEach((role) => {
			const roleKeys = getPermissionKey(selectedPort, role);
			roleKeys.forEach((key) => {
				portRoleBasedPermissions[key]?.forEach((permissionName) => {
					const permissionId = permissionNameToId[permissionName];
					if (
						permissionId &&
						!currentPermissionIds.includes(permissionId) &&
						!availablePermissionIds.includes(permissionId)
					) {
						availablePermissionIds.push(permissionId);
					}
				});
			});
		});

		// If role is selected, move only that role's permissions to current
		if (selectedRole) {
			const roleKeys = getPermissionKey(selectedPort, selectedRole);
			const rolePermissionIdsSet = new Set<string>();

			roleKeys.forEach((key) => {
				portRoleBasedPermissions[key]?.forEach((permissionName) => {
					const permissionId = permissionNameToId[permissionName];
					if (permissionId) {
						// In edit mode, only add to current if it's in the user's actual permissions from backend
						if (mode === 'edit' && userActualPermissions?.length > 0) {
							if (userActualPermissions.includes(permissionName)) {
								rolePermissionIdsSet.add(permissionId);
							}
							// If not in user's actual permissions, it stays in available (already added above)
						} else {
							rolePermissionIdsSet.add(permissionId);
						}
					}
				});
			});

			// Move role permissions from available to current (avoid duplicates)
			rolePermissionIdsSet.forEach((id) => {
				const index = availablePermissionIds.indexOf(id);
				if (index > -1) {
					availablePermissionIds.splice(index, 1);
				}
				if (!currentPermissionIds.includes(id)) {
					currentPermissionIds.push(id);
				}
			});
		}

		// // Remove 'Invoices:Visualize' from current permissions if admin roles are selected
		// if (selectedRole === 'Admin') {
		// 	const roleKeys = getPermissionKey(selectedPort, selectedRole);
		// 	const shouldRemoveInvoicesView =
		// 		roleKeys.includes('Admin-Woundcare+') ||
		// 		roleKeys.includes('Admin-SalesRep-MindSight-PaymentMSL-PaymentsWC+');

		// 	if (shouldRemoveInvoicesView) {
		// 		const invoicesViewPermissionId = permissionNameToId['Invoices:Visualize'];
		// 		if (invoicesViewPermissionId) {
		// 			const index = currentPermissionIds.indexOf(invoicesViewPermissionId);
		// 			if (index > -1) {
		// 				currentPermissionIds.splice(index, 1);
		// 				// Move it to available if not already there
		// 				if (!availablePermissionIds.includes(invoicesViewPermissionId)) {
		// 					availablePermissionIds.push(invoicesViewPermissionId);
		// 				}
		// 			}
		// 		}
		// 	}
		// }

		setPermissionsData((prev) => ({
			...prev,
			columns: {
				...prev.columns,
				'column-1': {
					...prev.columns['column-1'],
					itemIds: availablePermissionIds,
				},
				'column-2': {
					...prev.columns['column-2'],
					itemIds: currentPermissionIds,
				},
			},
		}));
	}, [
		selectedPort,
		selectedRole,
		fetchedPermissions,
		mode,
		initialData,
		portRoleMapping,
		hasPortRoleChanged,
	]);

	// Extract permission names from column-2 (Current Permissions) and set to form data
	useEffect(() => {
		const currentPermissionIds = permissionsData.columns['column-2'].itemIds;
		const permissionNames = currentPermissionIds
			.map((id) => permissionsData.item[id]?.content)
			.filter(Boolean) as string[];

		setValue('permissions', permissionNames, { 
			shouldDirty: isManualPermissionChange.current 
		});
		// Reset the flag after using it
		isManualPermissionChange.current = false;
	}, [permissionsData, setValue]);

	const handleSubmitForm = handleSubmit(async (data) => {
		await onSubmit(data);
	});

	useEffect(() => {
		if (onIsDirtyChange) {
			onIsDirtyChange(!isDirty);
		}
	}, [isDirty]);

	useEffect(() => {
		if (mode === 'create') {
			reset({}, { keepDirty: false });
			setHasPortRoleChanged(false);
			previousPortRef.current = '';
		} else if (initialData && Object.keys(initialData)?.length > 0) {
			reset(initialData, { keepDirty: false });
			setHasPortRoleChanged(false);
			previousPortRef.current = '';
		}
	}, [mode, initialData, reset]);

	return (
		<form onSubmit={handleSubmitForm}>
			<Grid
				templateColumns={{
					base: '1fr',
					md: '1fr 1fr 1fr',
				}}
				gap={{ base: 4, md: 6 }}
				pt={5}
			>
				<GridItem>
					<FaceliftInput
						topLabel="First Name"
						placeholder="Type First Name"
						{...register('firstName')}
						error={errors.firstName?.message}
						required
					/>
				</GridItem>

				<GridItem>
					<FaceliftInput
						topLabel="Last Name"
						placeholder="Type Last Name"
						{...register('lastName')}
						error={errors.lastName?.message}
						required
					/>
				</GridItem>

				<GridItem>
					<FaceliftInput
						topLabel="Email"
						placeholder="Email Address"
						type="email"
						{...register('email')}
						error={errors.email?.message}
						required
						disabled={mode === 'edit'}
					/>
				</GridItem>

				<GridItem>
					<FaceliftSelect
						topLabel="Port"
						placeholder="Select Port"
						value={
							portItems.find((item) => item.value === watch('port')) || null
						}
						onChange={(e) => {
							const newPort = (e?.value as string) || '';
							setValue('port', newPort, { shouldDirty: true });
							
							if (mode === 'edit') {
								setHasPortRoleChanged(true);
							}
						}}
						items={portItems}
						error={errors.port?.message}
						isSearchable={false}
						required
					/>
					<Show when={selectedPort === 'SalesRep'}>
						<Box display="flex" alignItems="center" mt={2} gap={2}>
							<FaceliftText>Auto Sync Clinics</FaceliftText>
							<FaceliftSwitch
								onCheckedChange={(val: boolean) =>
									setValue('autoSyncClinics', val, { shouldDirty: true })
								}
								checked={watch('autoSyncClinics') || false}
								_dark={{
									border: '1px solid',
									borderColor: 'brand.600',
									borderRadius: 'full',
								}}
								_light={{
									border: '1px solid',
									borderColor: 'gray.200',
									borderRadius: 'full',
								}}
							/>
						</Box>
					</Show>
				</GridItem>

				<GridItem>
					<FaceliftSelect
						topLabel="Role"
						placeholder="Select Role"
						value={
							roleItems.find((item) => item.value === watch('role')) || null
						}
						onChange={(newValue: SingleValue<SelectItem>) => {
							setValue('role', (newValue?.value as string) || '', { shouldDirty: true });
							if (mode === 'edit') {
								setHasPortRoleChanged(true);
							}
						}}
						items={roleItems}
						error={errors.role?.message}
						isSearchable={false}
						isDisabled={!selectedPort}
						required
					/>
					<Box display="flex" alignItems="center" mt={2} gap={2}>
						<FaceliftText>Status</FaceliftText>
						<FaceliftSwitch
							onCheckedChange={(val: boolean) =>
								setValue('status', val ? 'enabled' : 'disabled', { shouldDirty: true })
							}
							checked={watch('status') === 'enabled'}
							_dark={{
								border: '1px solid',
								borderColor: 'brand.600',
								borderRadius: 'full',
							}}
							_light={{
								border: '1px solid',
								borderColor: 'gray.200',
								borderRadius: 'full',
							}}
						/>
					</Box>
				</GridItem>

				<GridItem>
					<FaceliftSelect
						topLabel="Environments"
						placeholder="Select Environments"
						value={
							environmentsItems.filter((item) =>
								watch('environments')?.includes(item.value as string),
							) || null
						}
						onChange={(newValue: MultiValue<SelectItem>) =>
							setValue(
								'environments',
								(newValue?.map((item) => item.value) as string[]) || [],
								{ shouldDirty: true }
							)
						}
						items={environmentsItems}
						error={errors.environments?.message}
						isSearchable={false}
						required
						isMulti={true}
					/>
				</GridItem>

				<GridItem>
					<FaceliftSelect<SelectItem, true>
						limit={15}
						topLabel="Clinics"
						placeholder="Select Clinics"
						value={watch('clinics') as SelectItem[]}
						onChange={(newValue: MultiValue<SelectItem>) =>
							setValue('clinics', newValue as unknown as SelectItem[], { shouldDirty: true })
						}
						items={clinics.items}
						loading={clinics.loading}
						error={clinics.error || errors.clinics?.message}
						onLoadMore={clinics.onLoadMore}
						required
						isMulti={true}
					/>
				</GridItem>

				<GridItem>
					<FaceliftSelect
						topLabel="User Type"
						placeholder="Select User Type"
						value={
							userTypeItems.find((item) => item.value === watch('userType')) ||
							null
						}
						onChange={(newValue: SingleValue<SelectItem>) =>
							setValue('userType', (newValue?.value as string) || '', { shouldDirty: true })
						}
						items={userTypeItems}
						error={errors.userType?.message}
						isSearchable={false}
						required
					/>
				</GridItem>

				<GridItem>
					<FaceliftSelect<SelectItem, true>
						limit={15}
						topLabel="Sales Reps"
						placeholder="Select Sales Rep"
						value={watch('salesReps') as SelectItem[]}
						onChange={(newValue: MultiValue<SelectItem>) =>
							setValue(
								'salesReps',
								newValue as unknown as SelectItem[],
								{ shouldDirty: true }
							)
						}
						items={selectedPort === 'SalesRep' ? salesReps.items : []}
						loading={salesReps.loading}
						error={salesReps.error || errors.salesReps?.message}
						onLoadMore={salesReps.onLoadMore}
						isDisabled={selectedPort !== 'SalesRep'}
						required={selectedPort === 'SalesRep'}
						isMulti={true}
					/>
				</GridItem>

				<GridItem>
					<FaceliftSelect
						limit={15}
						topLabel="Consignments"
						placeholder="Select Consignments"
						value={
							selectedPort === 'SalesRep'
								? locations.items.filter((item) =>
										watch('consignments')?.some((consignment) => (consignment as SelectItem).value === item.value),
									) || null
								: null
						}
						onChange={(newValue: MultiValue<SelectItem>) =>
							setValue(
								'consignments',
								newValue as unknown as SelectItem[],
								{ shouldDirty: true }
							)
						}
						items={selectedPort === 'SalesRep' ? locations.items : []}
						loading={locations.loading}
						error={locations.error || errors.consignments?.message}
						onLoadMore={locations.onLoadMore}
						isDisabled={selectedPort !== 'SalesRep'}
						isMulti={true}
					/>
				</GridItem>

				<GridItem colSpan={{ base: 1, md: 1 }}>
					<VStack alignItems={'start'}>
						<FaceliftText fontSize="sm" fontStyle={'italic'}>
							Notifications:
						</FaceliftText>
						<Box display="flex" alignItems="center" mt={2} gap={2}>
							<FaceliftText>
								{watch('notificationsEnabled') === true
									? 'Enabled'
									: 'Disabled'}
							</FaceliftText>
							<FaceliftSwitch
								checked={watch('notificationsEnabled')}
								onCheckedChange={(val) => {
									setValue('notificationsEnabled', val, { shouldDirty: true });
								}}
								_dark={{
									border: '1px solid',
									borderColor: 'brand.600',
									borderRadius: 'full',
								}}
								_light={{
									border: '1px solid',
									borderColor: 'gray.200',
									borderRadius: 'full',
								}}
							/>
						</Box>
					</VStack>
				</GridItem>
				<Show when={watch('notificationsEnabled')}>
					<GridItem colSpan={{ base: 1, md: 2 }}>
						<FaceliftSelect
							topLabel="Notification Modules"
							items={notificationTypesItems}
							value={
								notificationTypesItems.filter((item) =>
									(watch('notificationTypes') as UserNotificationItem[])?.some(
										(type) => type.module === item.value,
									),
								) || []
							}
							onChange={(value) => {
								const newValue = value as MultiValue<SelectItem>;
								const currentUserNotificationTypes =
									(getValues('notificationTypes') as UserNotificationItem[]) ||
									[];
								const updatedUserNotificationTypes = newValue
									.map((item) => {
										const existing = currentUserNotificationTypes.find(
											(t) => t.module === item.value,
										);
										if (existing) {
											return existing;
										}
										return {
											...notificationTypesManagement[item.value],
											enabled: true,
											methods: notificationTypesManagement[
												item.value
											].methods.map((method) => ({ ...method, enabled: true })),
										};
									})
									.sort((a, b) => a.module.localeCompare(b.module));

								setValue('notificationTypes', updatedUserNotificationTypes, { shouldDirty: true });
							}}
							loading={isNotificationTypesFetching}
							isMulti={true}
							placeholder="Select notification types"
						/>

						<Show when={watch('notificationTypes')?.length > 0}>
							<Box mt={6}>
								<FaceliftText fontWeight="bold" fontSize="md" mb={4}>
									Notification Management
								</FaceliftText>
								<Flex wrap="wrap" gap={8}>
									<Flex
										align="start"
										flexDirection="column"
										gap={{ base: 6, md: 4 }}
									>
										{(
											(watch('notificationTypes') as UserNotificationItem[]) ||
											[]
										).map((type: UserNotificationItem, typeIndex: number) => (
											<Flex
												key={type.module}
												gap={{ base: 3, md: 10 }}
												flexDirection={{ base: 'column', md: 'row' }}
											>
												<Flex
													align="center"
													justifyContent="space-between"
													width="250px"
												>
													<FaceliftText fontSize={'sm'}>
														{type.module}
													</FaceliftText>
													<FaceliftSwitch
														checked={type.enabled}
														onCheckedChange={(val) => {
															const currentTypes =
																(getValues(
																	'notificationTypes',
																) as UserNotificationItem[]) || [];
															const updatedTypes = [...currentTypes];
															updatedTypes[typeIndex] = {
																...updatedTypes[typeIndex],
																enabled: val,
															};
															setValue('notificationTypes', updatedTypes, { shouldDirty: true });
														}}
													/>
												</Flex>
												{type.enabled && type.methods?.length > 0 && (
													<Flex gap={{ base: 10, md: 20 }}>
														{type.methods.map(
															(
																method: UserNotificationMethod,
																methodIndex: number,
															) => (
																<FaceliftCheckbox
																	key={`${type.module}-${method.name}`}
																	checked={method.enabled}
																	onCheckedChange={(checked: boolean) => {
																		const currentTypes =
																			(getValues(
																				'notificationTypes',
																			) as UserNotificationItem[]) || [];
																		const updatedTypes = [...currentTypes];
																		const updatedMethods = [
																			...updatedTypes[typeIndex].methods,
																		];
																		updatedMethods[methodIndex] = {
																			...updatedMethods[methodIndex],
																			enabled: !!checked,
																		};
																		updatedTypes[typeIndex] = {
																			...updatedTypes[typeIndex],
																			methods: updatedMethods,
																		};
																		setValue('notificationTypes', updatedTypes, { shouldDirty: true });
																	}}
																	label={method.name}
																	size="xs"
																/>
															),
														)}
													</Flex>
												)}
											</Flex>
										))}
									</Flex>
								</Flex>
							</Box>
						</Show>
					</GridItem>
				</Show>

				<GridItem colSpan={{ base: 1, md: 3 }}>
					<FaceliftText fontWeight="bold" fontSize="md" mb={3} mt={4}>
						Permissions
					</FaceliftText>
					<FaceliftDraggable
						data={permissionsData}
						onChange={(newData) => {
							isManualPermissionChange.current = true;
							setPermissionsData(newData);
						}}
						isLoading={isPermissionsFetching}
						height="200px"
					/>
				</GridItem>

				<GridItem colSpan={{ base: 1, md: 3 }}>
					<Box
						display="flex"
						flexDirection="row"
						gap={3}
						justifyContent="flex-end"
						width="full"
						mt={4}
					>
						<FaceliftButton
							width={{ base: 'full', md: '200px' }}
							loading={isLoading}
							type="submit"
						>
							Submit
						</FaceliftButton>
					</Box>
				</GridItem>
			</Grid>
		</form>
	);
};

export default UsersRolesForm;
